import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Card, Collapse, CardHeader, IconButton } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { FormLabel, FormControl, FormControlLabel, FormGroup, FormHelperText, Checkbox, Typography } from '@material-ui/core';
import getPermissions from './config/index';

/*  For an in-depth explanation of the PermissionSelector process. 
    Visit the Milkmoovement-Fuse Wiki on Github.    */
const PermissionSelector = ({ permissionType, handlePermissionChange, selectedValues, disabled, showError, hideHeader }) => {
    const { permissionGroups, requiredPermissions } = getPermissions(permissionType);
    const [selectedPermissions, setSelectedPermissions] = useState(() => new Set([...selectedValues, ...requiredPermissions]));
    const [openGroups, setOpenGroups] = useState({});
    const prevSelectedValuesRef = useRef(selectedValues);

    useEffect(() => {
        const prev = new Set(prevSelectedValuesRef.current || []);
        const curr = new Set(selectedValues || []);

        if (prev.size !== curr.size || [...prev].some((v) => !curr.has(v))) {
            prevSelectedValuesRef.current = selectedValues;
            setSelectedPermissions(new Set([...selectedValues, ...requiredPermissions]));
        }
    }, [selectedValues, requiredPermissions]);

    const allPermissionValues = useMemo(() => permissionGroups.flatMap((g) => g.permissions.map((p) => p.value)), [permissionGroups]);

    const isRequired = (p) => requiredPermissions.has(p);
    const isSelected = (p) => selectedPermissions.has(p);
    const getGroupStats = (group) => {
        const selected = group.permissions.filter(({ value }) => isSelected(value)).length;
        return { selected, total: group.permissions.length, checked: selected === group.permissions.length };
    };

    const selectAll = allPermissionValues.every((value) => selectedPermissions.has(value));
    const error = !selectedPermissions.size;

    const updatePermissions = (values, checked) => {
        setSelectedPermissions((prev) => {
            const next = new Set(prev);
            values.forEach((value) => {
                if (checked) {
                    next.add(value);
                } else if (!isRequired(value)) {
                    next.delete(value);
                }
            });
            handlePermissionChange(next);
            return next;
        });
    };

    const handleToggleGroup = (label) => () => setOpenGroups((prev) => ({ ...prev, [label]: !prev[label] }));
    const handleSelectPermission = (permission) => (e) => updatePermissions([permission], e.target.checked);
    const handleSelectGroup = (group) => (e) =>
        updatePermissions(
            group.permissions.map((p) => p.value),
            e.target.checked
        );
    const handleSelectAll = (e) => {
        const allValues = permissionGroups.flatMap((g) => g.permissions.map((p) => p.value));
        updatePermissions(allValues, e.target.checked);
    };

    return (
        <FormControl error={error && showError} component="fieldset">
            {!!hideHeader && (
                <FormLabel className="flex flex-row justify-center">
                    <Typography variant="h6" className="mt-16 mb-16 justify-center">
                        Permissions
                    </Typography>
                </FormLabel>
            )}
            <FormControlLabel control={<Checkbox checked={selectAll} onChange={handleSelectAll} />} label="Select All" />
            <Card>
                {permissionGroups.map((group) => {
                    const { selected, total, checked } = getGroupStats(group);
                    const open = openGroups[group.label] || false;
                    return (
                        <div key={group.label} style={{ boxShadow: '1px 1px 1px 1px rgba(0,0,0,0.1)', paddingBottom: '1px' }}>
                            <CardHeader
                                titleTypographyProps={{ variant: 'body1' }}
                                title={group.label}
                                action={
                                    <div className="flex flex-row justify-center">
                                        <Typography variant="body1" style={{ marginTop: 5 }}>
                                            {selected}/{total} selected
                                        </Typography>
                                        <IconButton onClick={handleToggleGroup(group.label)} aria-label="expand" size="small">
                                            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                        </IconButton>
                                    </div>
                                }
                            />
                            <Collapse in={open} timeout="auto" unmountOnExit>
                                <FormGroup>
                                    <FormControlLabel style={{ backgroundColor: '#FAFAFA', paddingLeft: '15px', marginRight: 0 }} disabled={disabled} control={<Checkbox checked={checked} onChange={handleSelectGroup(group)} />} label="Select All" />
                                    {group.permissions.map((p, i) => (
                                        <FormControlLabel
                                            key={`${p.label}-select`}
                                            style={{
                                                backgroundColor: i % 2 === 0 ? '#FFFFFF' : '#FAFAFA',
                                                paddingLeft: '15px',
                                                marginRight: 0,
                                            }}
                                            disabled={isRequired(p.value) || disabled}
                                            control={<Checkbox checked={isSelected(p.value)} onChange={handleSelectPermission(p.value)} value={p.value} />}
                                            label={p.label}
                                        />
                                    ))}
                                </FormGroup>
                            </Collapse>
                        </div>
                    );
                })}
            </Card>
            {error && showError && <FormHelperText>* Please select at least one permission.</FormHelperText>}
        </FormControl>
    );
};

export default PermissionSelector;
